<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisCondominiosAPI from 'dashboard/api/laisCondominios';
import {
  formatMoney,
  listingLinkState,
  normalizeCondominium,
  normalizeCondominiumsResponse,
  normalizeListingsResponse,
  statusLabel,
} from '../utils/condominiosPresenter';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const firstQueryValue = value => (Array.isArray(value) ? value[0] : value);

const query = ref(firstQueryValue(route.query.q) || '');
const condominiums = ref([]);
const selectedId = ref(null);
const listings = ref([]);
const listingResults = ref([]);
const listingQuery = ref('');
const auditReason = ref('');
const isLoading = ref(false);
const detailLoading = ref(false);
const listingsLoading = ref(false);
const listingSearchLoading = ref(false);
const mutationLoading = ref(false);
const loadError = ref('');
const lastAudit = ref(null);
const listMeta = ref({});

const selectedCondominium = computed(
  () =>
    condominiums.value.find(
      condominium => String(condominium.id) === String(selectedId.value)
    ) || null
);

const totalCount = computed(
  () => Number(listMeta.value.count || condominiums.value.length) || 0
);
const readyCount = computed(
  () => condominiums.value.filter(condominium => condominium.publicable).length
);
const conflictCount = computed(
  () =>
    condominiums.value.filter(condominium => condominium.conflicts.length)
      .length
);
const emptyListingsCount = computed(
  () =>
    condominiums.value.filter(
      condominium => condominium.listingsAvailable === 0
    ).length
);
const metrics = computed(() => [
  {
    label: t('CONDOMINIUMS.METRICS.TOTAL'),
    value: totalCount.value,
    icon: 'i-lucide-building-2',
  },
  {
    label: t('CONDOMINIUMS.METRICS.PUBLICABLE'),
    value: readyCount.value,
    icon: 'i-lucide-globe-2',
  },
  {
    label: t('CONDOMINIUMS.METRICS.CONFLICTS'),
    value: conflictCount.value,
    icon: 'i-lucide-triangle-alert',
  },
  {
    label: t('CONDOMINIUMS.METRICS.EMPTY_LISTINGS'),
    value: emptyListingsCount.value,
    icon: 'i-lucide-link-2-off',
  },
]);

const canMutateListing = computed(() => {
  const permissions = selectedCondominium.value?.permissions || {};
  return permissions.can_link_listing !== false;
});
const fallbackCopy = computed(() => ({
  INCOMPLETE_ADDRESS: t('CONDOMINIUMS.FALLBACK.INCOMPLETE_ADDRESS'),
  PENDING_ADDRESS: t('CONDOMINIUMS.FALLBACK.PENDING_ADDRESS'),
}));

const condominiumName = condominium =>
  condominium?.nome || t('CONDOMINIUMS.FALLBACK.CONDOMINIUM_NAME');
const listingTitle = listing =>
  listing?.titulo || t('CONDOMINIUMS.FALLBACK.LISTING_TITLE');
const addressLabel = (value, fallbackKey) =>
  value || fallbackCopy.value[fallbackKey] || '';
const translateStatus = status => statusLabel(status, t);
const formatPercent = value =>
  t('CONDOMINIUMS.FORMAT.PERCENT', { value: Number(value) || 0 });

const updateRouteQuery = () => {
  router.replace({
    name: 'lais_condominios_index',
    params: route.params,
    query: { ...route.query, q: query.value || undefined },
  });
};

const replaceCondominium = condominium => {
  const normalized = normalizeCondominium(condominium);
  const index = condominiums.value.findIndex(
    item => String(item.id) === String(normalized.id)
  );

  if (index >= 0) {
    condominiums.value.splice(index, 1, normalized);
    return;
  }

  condominiums.value.unshift(normalized);
};

const loadListings = async () => {
  if (!selectedId.value) return;
  listingsLoading.value = true;
  try {
    const response = await LaisCondominiosAPI.listings(selectedId.value);
    listings.value = normalizeListingsResponse(response).listings;
  } catch {
    useAlert(t('CONDOMINIUMS.ALERTS.LISTINGS_LOAD_FAILED'));
  } finally {
    listingsLoading.value = false;
  }
};

const loadDetails = async () => {
  if (!selectedId.value) return;
  detailLoading.value = true;
  try {
    const response = await LaisCondominiosAPI.show(selectedId.value);
    replaceCondominium(response.data);
  } catch {
    useAlert(t('CONDOMINIUMS.ALERTS.DETAIL_LOAD_FAILED'));
  } finally {
    detailLoading.value = false;
  }
};

const selectCondominium = async condominium => {
  selectedId.value = condominium?.id || null;
  listingResults.value = [];
  lastAudit.value = null;
  await Promise.all([loadDetails(), loadListings()]);
};

const loadCondominiums = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    const response = await LaisCondominiosAPI.get({
      q: query.value,
      page: 1,
    });
    const normalized = normalizeCondominiumsResponse(response);
    condominiums.value = normalized.condominiums;
    listMeta.value = normalized.meta;
    updateRouteQuery();

    if (
      !selectedId.value ||
      !condominiums.value.some(
        item => String(item.id) === String(selectedId.value)
      )
    ) {
      selectedId.value = condominiums.value[0]?.id || null;
    }

    await Promise.all([loadDetails(), loadListings()]);
  } catch {
    loadError.value = t('CONDOMINIUMS.ERRORS.LOAD');
  } finally {
    isLoading.value = false;
  }
};

const searchListingCandidates = async () => {
  if (listingQuery.value.trim().length < 2) {
    useAlert(t('CONDOMINIUMS.ALERTS.SEARCH_QUERY_REQUIRED'));
    return;
  }

  listingSearchLoading.value = true;
  try {
    const response = await LaisCondominiosAPI.searchListings({
      q: listingQuery.value,
      page: 1,
    });
    listingResults.value = normalizeListingsResponse(response).listings;
  } catch {
    useAlert(t('CONDOMINIUMS.ALERTS.LISTING_SEARCH_FAILED'));
  } finally {
    listingSearchLoading.value = false;
  }
};

const requireAuditReason = () => {
  if (auditReason.value.trim().length >= 6) return true;

  useAlert(t('CONDOMINIUMS.ALERTS.AUDIT_REASON_REQUIRED'));
  return false;
};

const confirmAction = message => {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') {
    return true;
  }

  // eslint-disable-next-line no-alert -- FE-AUD-59 keeps native confirmation until the audited modal is promoted.
  return window.confirm(message);
};

const linkListing = async listing => {
  if (!selectedCondominium.value || !requireAuditReason()) return;

  const state = listingLinkState(listing, selectedCondominium.value.id);
  if (state === 'linked') {
    useAlert(t('CONDOMINIUMS.ALERTS.ALREADY_LINKED'));
    return;
  }

  const confirmMessage =
    state === 'linked_elsewhere'
      ? t('CONDOMINIUMS.CONFIRM.REASSIGN', {
          current:
            listing.condominiumName ||
            t('CONDOMINIUMS.FALLBACK.OTHER_CONDOMINIUM'),
          condominium: condominiumName(selectedCondominium.value),
        })
      : t('CONDOMINIUMS.CONFIRM.LINK', {
          listing: listingTitle(listing),
          condominium: condominiumName(selectedCondominium.value),
        });

  if (!confirmAction(confirmMessage)) return;

  mutationLoading.value = true;
  try {
    const response = await LaisCondominiosAPI.linkListing(
      selectedCondominium.value.id,
      {
        imovel_id: listing.id,
        audit_reason: auditReason.value,
      }
    );
    lastAudit.value = response.data?.audit || null;
    useAlert(t('CONDOMINIUMS.ALERTS.LINKED'));
    await Promise.all([
      loadDetails(),
      loadListings(),
      searchListingCandidates(),
    ]);
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || t('CONDOMINIUMS.ERRORS.LINK')
    );
  } finally {
    mutationLoading.value = false;
  }
};

const unlinkListing = async listing => {
  if (!selectedCondominium.value || !requireAuditReason()) return;
  if (
    !confirmAction(
      t('CONDOMINIUMS.CONFIRM.UNLINK', {
        listing: listingTitle(listing),
        condominium: condominiumName(selectedCondominium.value),
      })
    )
  ) {
    return;
  }

  mutationLoading.value = true;
  try {
    const response = await LaisCondominiosAPI.unlinkListing(
      selectedCondominium.value.id,
      listing.id,
      { audit_reason: auditReason.value }
    );
    lastAudit.value = response.data?.audit || null;
    useAlert(t('CONDOMINIUMS.ALERTS.UNLINKED'));
    await Promise.all([loadDetails(), loadListings()]);
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || t('CONDOMINIUMS.ERRORS.UNLINK')
    );
  } finally {
    mutationLoading.value = false;
  }
};

const listingPrice = listing =>
  formatMoney(listing.valorVenda || listing.valorAluguel);

onMounted(loadCondominiums);
</script>

<template>
  <main class="min-h-screen bg-lais-bg">
    <LaisHeader
      :title="t('CONDOMINIUMS.HEADER.TITLE')"
      :helper="t('CONDOMINIUMS.HEADER.HELPER')"
      title-badge="FE-AUD-59"
    >
      <template #actions>
        <form
          class="flex w-full min-w-0 items-center gap-2 md:w-[420px]"
          @submit.prevent="loadCondominiums"
        >
          <label class="relative min-w-0 flex-1">
            <span class="sr-only">
              {{ t('CONDOMINIUMS.SEARCH.LABEL') }}
            </span>
            <span
              class="i-lucide-search pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-lais-text-muted"
              aria-hidden="true"
            />
            <input
              v-model="query"
              type="search"
              class="min-h-10 w-full rounded-lais border border-lais-border bg-white py-2 pl-9 pr-3 text-sm text-lais-text outline-none transition focus:border-lais-primary focus:shadow-lais-focus"
              :placeholder="t('CONDOMINIUMS.SEARCH.PLACEHOLDER')"
            />
          </label>
          <LaisPrimaryButton
            icon="i-lucide-search"
            :is-loading="isLoading"
            type="submit"
          >
            {{ t('CONDOMINIUMS.ACTIONS.SEARCH') }}
          </LaisPrimaryButton>
        </form>
      </template>
    </LaisHeader>

    <section class="px-4 pb-8 pt-4 sm:px-6 lg:px-10">
      <div class="grid gap-3 md:grid-cols-4">
        <div
          v-for="metric in metrics"
          :key="metric.label"
          class="flex min-h-[76px] items-center gap-3 rounded-lais border border-lais-border-soft bg-white px-4"
        >
          <span
            class="grid size-9 shrink-0 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
          >
            <span class="size-4" :class="[metric.icon]" aria-hidden="true" />
          </span>
          <div>
            <p class="text-[12px] font-semibold uppercase text-lais-text-muted">
              {{ metric.label }}
            </p>
            <p
              class="font-redHatDisplay text-[24px] font-semibold text-lais-text"
            >
              {{ metric.value }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="loadError"
        class="mt-4 rounded-lais border border-lais-danger/40 bg-lais-danger-50 px-4 py-3 text-sm text-lais-danger"
      >
        {{ loadError }}
      </div>

      <div class="mt-4 grid gap-4 xl:grid-cols-[minmax(320px,420px)_1fr]">
        <section class="min-w-0">
          <div class="mb-2 flex items-center justify-between gap-3">
            <h2 class="text-sm font-semibold text-lais-text">
              {{ t('CONDOMINIUMS.REVIEW_QUEUE.TITLE') }}
            </h2>
            <span class="text-xs text-lais-text-muted">
              {{
                t('CONDOMINIUMS.REVIEW_QUEUE.PAGE_COUNT', {
                  count: condominiums.length,
                })
              }}
            </span>
          </div>
          <div
            class="flex max-h-[calc(100vh-260px)] flex-col gap-2 overflow-y-auto pr-1"
          >
            <button
              v-for="condominium in condominiums"
              :key="condominium.id"
              type="button"
              class="rounded-lais border bg-white p-4 text-left transition hover:border-lais-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-lais-primary-50"
              :class="
                selectedId === condominium.id
                  ? 'border-lais-primary shadow-lais-card'
                  : 'border-lais-border-soft'
              "
              @click="selectCondominium(condominium)"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p
                    class="truncate font-redHatDisplay text-base font-semibold text-lais-text"
                  >
                    {{ condominiumName(condominium) }}
                  </p>
                  <p class="mt-1 line-clamp-2 text-xs text-lais-text-muted">
                    {{
                      addressLabel(
                        condominium.addressLabel,
                        'INCOMPLETE_ADDRESS'
                      )
                    }}
                  </p>
                </div>
                <span
                  class="shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold"
                  :class="
                    condominium.publicable
                      ? 'bg-lais-mint/15 text-lais-mint-dark'
                      : 'bg-lais-warning/15 text-lais-text'
                  "
                >
                  {{ formatPercent(condominium.score) }}
                </span>
              </div>
              <div class="mt-3 flex flex-wrap gap-2 text-[11px]">
                <span
                  class="rounded-full bg-lais-primary-50 px-2 py-1 text-lais-primary"
                >
                  {{
                    t('CONDOMINIUMS.REVIEW_QUEUE.LISTINGS_COUNT', {
                      count: condominium.listingsAvailable,
                    })
                  }}
                </span>
                <span
                  v-if="condominium.conflicts.length"
                  class="rounded-full bg-lais-danger-50 px-2 py-1 text-lais-danger"
                >
                  {{ t('CONDOMINIUMS.REVIEW_QUEUE.CONFLICT') }}
                </span>
                <span
                  v-if="!condominium.publicable"
                  class="rounded-full bg-lais-warning/15 px-2 py-1 text-lais-text"
                >
                  {{ t('CONDOMINIUMS.REVIEW_QUEUE.BLOCKER') }}
                </span>
              </div>
            </button>
            <div
              v-if="!isLoading && !condominiums.length"
              class="rounded-lais border border-dashed border-lais-border bg-white px-4 py-8 text-center text-sm text-lais-text-muted"
            >
              {{ t('CONDOMINIUMS.EMPTY.SEARCH') }}
            </div>
          </div>
        </section>

        <section class="min-w-0">
          <div
            v-if="!selectedCondominium"
            class="rounded-lais border border-dashed border-lais-border bg-white px-4 py-12 text-center text-sm text-lais-text-muted"
          >
            {{ t('CONDOMINIUMS.EMPTY.SELECT') }}
          </div>

          <div v-else class="grid gap-4">
            <LaisCard density="cozy">
              <template #header>
                <div class="min-w-0">
                  <p
                    class="font-redHatDisplay text-xl font-semibold text-lais-text"
                  >
                    {{ condominiumName(selectedCondominium) }}
                  </p>
                  <p class="mt-1 text-sm text-lais-text-muted">
                    {{
                      addressLabel(
                        selectedCondominium.addressLabel,
                        'PENDING_ADDRESS'
                      )
                    }}
                  </p>
                </div>
                <a
                  v-if="selectedCondominium.publicUrl"
                  :href="selectedCondominium.publicUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex size-9 items-center justify-center rounded-full border border-lais-border text-lais-text-muted transition hover:border-lais-primary hover:text-lais-primary"
                  :aria-label="t('CONDOMINIUMS.ACTIONS.OPEN_PUBLIC_PAGE')"
                >
                  <span
                    class="i-lucide-external-link size-4"
                    aria-hidden="true"
                  />
                </a>
              </template>

              <div class="grid gap-3 md:grid-cols-3">
                <div class="rounded-lais bg-lais-primary-50/50 p-3">
                  <p
                    class="text-xs font-semibold uppercase text-lais-text-muted"
                  >
                    {{ t('CONDOMINIUMS.DETAIL.QUALITY') }}
                  </p>
                  <p
                    class="mt-1 font-redHatDisplay text-2xl font-semibold text-lais-text"
                  >
                    {{ formatPercent(selectedCondominium.score) }}
                  </p>
                  <p class="text-xs text-lais-text-muted">
                    {{ translateStatus(selectedCondominium.qualityStatus) }}
                  </p>
                </div>
                <div class="rounded-lais bg-lais-primary-50/50 p-3">
                  <p
                    class="text-xs font-semibold uppercase text-lais-text-muted"
                  >
                    {{ t('CONDOMINIUMS.DETAIL.PUBLICATION') }}
                  </p>
                  <p
                    class="mt-1 font-redHatDisplay text-lg font-semibold text-lais-text"
                  >
                    {{ translateStatus(selectedCondominium.publicState) }}
                  </p>
                  <p class="text-xs text-lais-text-muted">
                    {{
                      selectedCondominium.publicable
                        ? t('CONDOMINIUMS.DETAIL.NO_CRITICAL_BLOCKERS')
                        : t('CONDOMINIUMS.DETAIL.PROMOTION_BLOCKED')
                    }}
                  </p>
                </div>
                <div class="rounded-lais bg-lais-primary-50/50 p-3">
                  <p
                    class="text-xs font-semibold uppercase text-lais-text-muted"
                  >
                    {{ t('CONDOMINIUMS.DETAIL.LISTINGS') }}
                  </p>
                  <p
                    class="mt-1 font-redHatDisplay text-2xl font-semibold text-lais-text"
                  >
                    {{ selectedCondominium.listingsAvailable }}
                  </p>
                  <p class="text-xs text-lais-text-muted">
                    {{ t('CONDOMINIUMS.DETAIL.PUBLIC_ACTIVE') }}
                  </p>
                </div>
              </div>

              <div
                v-if="selectedCondominium.conflicts.length"
                class="mt-4 rounded-lais border border-lais-danger/40 bg-lais-danger-50 p-4"
              >
                <div class="flex gap-3">
                  <span
                    class="i-lucide-triangle-alert mt-0.5 size-4 shrink-0 text-lais-danger"
                    aria-hidden="true"
                  />
                  <div class="min-w-0">
                    <p class="text-sm font-semibold text-lais-danger">
                      {{ t('CONDOMINIUMS.CONFLICTS.TITLE') }}
                    </p>
                    <ul class="mt-2 space-y-1 text-sm text-lais-danger">
                      <li
                        v-for="conflict in selectedCondominium.conflicts"
                        :key="conflict.id"
                      >
                        {{ conflict.nome }}
                        {{ t('CONDOMINIUMS.PUNCTUATION.DOT_SEPARATOR') }}
                        {{ addressLabel(conflict.endereco, 'PENDING_ADDRESS') }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="mt-4 grid gap-4 lg:grid-cols-2">
                <div>
                  <h3 class="text-sm font-semibold text-lais-text">
                    {{ t('CONDOMINIUMS.DETAIL.BLOCKERS_WARNINGS') }}
                  </h3>
                  <div class="mt-2 flex flex-col gap-2">
                    <p
                      v-if="
                        !selectedCondominium.blockers.length &&
                        !selectedCondominium.warnings.length
                      "
                      class="rounded-lais bg-lais-mint/15 px-3 py-2 text-sm text-lais-mint-dark"
                    >
                      {{ t('CONDOMINIUMS.DETAIL.READY_FOR_PUBLIC_PROMOTION') }}
                    </p>
                    <p
                      v-for="blocker in selectedCondominium.blockers"
                      :key="`blocker-${blocker.key}`"
                      class="rounded-lais bg-lais-danger-50 px-3 py-2 text-sm text-lais-danger"
                    >
                      {{ blocker.label }}
                    </p>
                    <p
                      v-for="warning in selectedCondominium.warnings"
                      :key="`warning-${warning.key}`"
                      class="rounded-lais bg-lais-warning/15 px-3 py-2 text-sm text-lais-text"
                    >
                      {{ warning.label }}
                    </p>
                  </div>
                </div>
                <div>
                  <h3 class="text-sm font-semibold text-lais-text">
                    {{ t('CONDOMINIUMS.DETAIL.SEO_ALIASES') }}
                  </h3>
                  <dl class="mt-2 space-y-2 text-sm">
                    <div>
                      <dt
                        class="text-xs font-semibold uppercase text-lais-text-muted"
                      >
                        {{ t('CONDOMINIUMS.DETAIL.SEO_TITLE') }}
                      </dt>
                      <dd class="text-lais-text">
                        {{
                          selectedCondominium.seo.title ||
                          t('CONDOMINIUMS.FALLBACK.PENDING')
                        }}
                      </dd>
                    </div>
                    <div>
                      <dt
                        class="text-xs font-semibold uppercase text-lais-text-muted"
                      >
                        {{ t('CONDOMINIUMS.DETAIL.CANONICAL') }}
                      </dt>
                      <dd class="break-all text-lais-text">
                        {{
                          selectedCondominium.seo.canonicalPath ||
                          t('CONDOMINIUMS.FALLBACK.PENDING')
                        }}
                      </dd>
                    </div>
                    <div>
                      <dt
                        class="text-xs font-semibold uppercase text-lais-text-muted"
                      >
                        {{ t('CONDOMINIUMS.DETAIL.ALIASES') }}
                      </dt>
                      <dd class="flex flex-wrap gap-1">
                        <span
                          v-for="aliasName in selectedCondominium.aliases"
                          :key="aliasName"
                          class="rounded-full bg-lais-primary-50 px-2 py-1 text-xs text-lais-primary"
                        >
                          {{ aliasName }}
                        </span>
                        <span
                          v-if="!selectedCondominium.aliases.length"
                          class="text-lais-text-muted"
                        >
                          {{ t('CONDOMINIUMS.EMPTY.ALIASES') }}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </LaisCard>

            <LaisCard
              :title="t('CONDOMINIUMS.LINKED_LISTINGS.TITLE')"
              density="tight"
            >
              <template #titleAction>
                <span
                  v-if="detailLoading || listingsLoading"
                  class="i-lucide-loader-circle size-4 animate-spin text-lais-text-muted"
                  aria-hidden="true"
                />
              </template>
              <div class="grid gap-2">
                <article
                  v-for="listing in listings"
                  :key="listing.id"
                  class="flex min-w-0 flex-col gap-3 rounded-lais border border-lais-border-soft p-3 md:flex-row md:items-center md:justify-between"
                >
                  <div class="min-w-0">
                    <p class="font-semibold text-lais-text">
                      {{ listingTitle(listing) }}
                    </p>
                    <p class="text-xs text-lais-text-muted">
                      {{
                        [listing.endereco, listing.bairro, listing.cidade]
                          .filter(Boolean)
                          .join(', ') ||
                        t('CONDOMINIUMS.FALLBACK.PENDING_ADDRESS')
                      }}
                    </p>
                    <p class="mt-1 text-xs text-lais-text-muted">
                      {{ listing.tipo }}
                      {{ t('CONDOMINIUMS.PUNCTUATION.DOT_SEPARATOR') }}
                      {{ listing.finalidade }}
                      {{ t('CONDOMINIUMS.PUNCTUATION.DOT_SEPARATOR') }}
                      {{ listing.status }}
                      <span v-if="listingPrice(listing)">
                        {{ t('CONDOMINIUMS.PUNCTUATION.DOT_SEPARATOR') }}
                        {{ listingPrice(listing) }}
                      </span>
                    </p>
                  </div>
                  <LaisPrimaryButton
                    icon="i-lucide-link-2-off"
                    variant="ghost"
                    size="sm"
                    :disabled="!canMutateListing || mutationLoading"
                    @click="unlinkListing(listing)"
                  >
                    {{ t('CONDOMINIUMS.ACTIONS.UNLINK') }}
                  </LaisPrimaryButton>
                </article>
                <div
                  v-if="!listingsLoading && !listings.length"
                  class="rounded-lais border border-dashed border-lais-border px-4 py-8 text-center text-sm text-lais-text-muted"
                >
                  {{ t('CONDOMINIUMS.EMPTY.LINKED_LISTINGS') }}
                </div>
              </div>
            </LaisCard>

            <LaisCard
              :title="t('CONDOMINIUMS.LINK_FORM.TITLE')"
              density="tight"
            >
              <div class="grid gap-3 lg:grid-cols-[1fr_1fr_auto]">
                <label>
                  <span
                    class="mb-1 block text-xs font-semibold uppercase text-lais-text-muted"
                  >
                    {{ t('CONDOMINIUMS.LINK_FORM.SEARCH_LABEL') }}
                  </span>
                  <input
                    v-model="listingQuery"
                    type="search"
                    class="min-h-10 w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-sm outline-none focus:border-lais-primary focus:shadow-lais-focus"
                    :placeholder="
                      t('CONDOMINIUMS.LINK_FORM.SEARCH_PLACEHOLDER')
                    "
                    @keyup.enter="searchListingCandidates"
                  />
                </label>
                <label>
                  <span
                    class="mb-1 block text-xs font-semibold uppercase text-lais-text-muted"
                  >
                    {{ t('CONDOMINIUMS.LINK_FORM.AUDIT_REASON_LABEL') }}
                  </span>
                  <input
                    v-model="auditReason"
                    type="text"
                    class="min-h-10 w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-sm outline-none focus:border-lais-primary focus:shadow-lais-focus"
                    :placeholder="
                      t('CONDOMINIUMS.LINK_FORM.AUDIT_REASON_PLACEHOLDER')
                    "
                  />
                </label>
                <div class="flex items-end">
                  <LaisPrimaryButton
                    icon="i-lucide-search"
                    :is-loading="listingSearchLoading"
                    @click="searchListingCandidates"
                  >
                    {{ t('CONDOMINIUMS.ACTIONS.FIND') }}
                  </LaisPrimaryButton>
                </div>
              </div>

              <div
                v-if="lastAudit"
                class="mt-3 rounded-lais bg-lais-mint/15 px-3 py-2 text-sm text-lais-mint-dark"
              >
                {{
                  t('CONDOMINIUMS.LINK_FORM.LAST_AUDIT', {
                    action: lastAudit.action,
                    reason: lastAudit.reason,
                  })
                }}
              </div>

              <div class="mt-4 grid gap-2">
                <article
                  v-for="listing in listingResults"
                  :key="listing.id"
                  class="flex min-w-0 flex-col gap-3 rounded-lais border border-lais-border-soft p-3 md:flex-row md:items-center md:justify-between"
                >
                  <div class="min-w-0">
                    <p class="font-semibold text-lais-text">
                      {{ listingTitle(listing) }}
                    </p>
                    <p class="text-xs text-lais-text-muted">
                      {{
                        [listing.endereco, listing.bairro, listing.cidade]
                          .filter(Boolean)
                          .join(', ') ||
                        t('CONDOMINIUMS.FALLBACK.PENDING_ADDRESS')
                      }}
                    </p>
                    <p
                      v-if="
                        listingLinkState(listing, selectedCondominium.id) ===
                        'linked_elsewhere'
                      "
                      class="mt-1 text-xs text-lais-warning"
                    >
                      {{
                        t('CONDOMINIUMS.LINK_FORM.LINKED_ELSEWHERE', {
                          condominium:
                            listing.condominiumName ||
                            t('CONDOMINIUMS.FALLBACK.OTHER_CONDOMINIUM'),
                        })
                      }}
                    </p>
                  </div>
                  <LaisPrimaryButton
                    icon="i-lucide-link-2"
                    size="sm"
                    :variant="
                      listingLinkState(listing, selectedCondominium.id) ===
                      'linked'
                        ? 'ghost'
                        : 'primary'
                    "
                    :disabled="
                      !canMutateListing ||
                      mutationLoading ||
                      listingLinkState(listing, selectedCondominium.id) ===
                        'linked'
                    "
                    @click="linkListing(listing)"
                  >
                    {{
                      listingLinkState(listing, selectedCondominium.id) ===
                      'linked'
                        ? t('CONDOMINIUMS.ACTIONS.LINKED')
                        : t('CONDOMINIUMS.ACTIONS.LINK')
                    }}
                  </LaisPrimaryButton>
                </article>
                <div
                  v-if="
                    !listingSearchLoading &&
                    listingQuery &&
                    !listingResults.length
                  "
                  class="rounded-lais border border-dashed border-lais-border px-4 py-6 text-center text-sm text-lais-text-muted"
                >
                  {{ t('CONDOMINIUMS.EMPTY.LISTING_SEARCH') }}
                </div>
              </div>
            </LaisCard>
          </div>
        </section>
      </div>
    </section>
  </main>
</template>
